/**
 * Webform status/mail feedback under the form (AJAX / full postback).
 *
 * Library ssks/form_webform_feedback — attached on every form of opt-in
 * sites / webform confirmation. Hors webform, le formulaire envoyé est
 * retrouvé au rechargement (rememberSubmittedForm). Mutes Drupal toasts
 * when relocating. Skips when field errors are present (the summary owns
 * that path). Error toast wins over success if both exist.
 *
 * Annonce SR : lien focusé une fois, AJAX comme postback (testé en réel).
 * - Une live region injectée au chargement complet est ignorée (le SR lit
 *   la page depuis le haut : cookies, skip-links…). Le focus interrompt
 *   cette lecture, et un élément interactif (lien) arrête la lecture après
 *   le message — un bloc non interactif enchaîne sur le reste de la page.
 * - En AJAX, la commande webformScrollTop (webform.ajax.js) focuse le
 *   bookmark <div id="…-content" tabindex="-1"> qui entoure le formulaire :
 *   le SR lisait alors tout le formulaire (et noyait Drupal.announce). On
 *   retire ce tabindex à chaque attach (avant l'exécution de la commande,
 *   qui suit l'insert) : trigger('focus') sur un div non focusable est
 *   inoffensif, et notre lien focusé remplace le rôle du bookmark.
 * - Webform anime aussi un scroll (500 ms) vers le haut du formulaire :
 *   on re-scrolle vers le bandeau une fois l'animation finie.
 */
import {
  ERROR_TOAST_SELECTOR,
  FEEDBACK_CLASS,
  FIELD_ERROR_SELECTOR,
  SUCCESS_TOAST_SELECTOR,
  claimFocus,
  createScheduler,
  findWebformFeedbackHost,
  isSafeDomId,
  muteRelocatableToasts,
  readToastText,
  rememberSubmittedForm,
  safeDomToken,
  safeHashHref,
  scrollBelowSticky,
} from "./form-a11y-shared";

(function (Drupal, $) {
  /** @type {string} */
  let lastFeedbackSignature = "";

  /**
   * Handle déclenché par un ajaxComplete : sert au re-scroll différé
   * (l'animation webformScrollTop remonte en haut du formulaire).
   */
  let ajaxPhase = false;

  /**
   * @param {"success"|"error"} kind
   * @param {string} text
   * @return {string}
   */
  function feedbackSignature(kind, text) {
    return `${kind}:${text}`;
  }

  /**
   * Désarme le bookmark focusable du wrapper AJAX webform.
   *
   * webformScrollTop focuse <div id="…-content" tabindex="-1"> après chaque
   * rendu AJAX (voir en-tête de fichier). Le wrapper est re-rendu à chaque
   * réponse : à rejouer à chaque attach (l'attach de l'insert précède la
   * commande webformScrollTop, le retrait est donc effectif à temps).
   */
  function disarmWebformFocusBookmark() {
    document
      .querySelectorAll(".webform-ajax-form-wrapper")
      .forEach((wrapper) => {
        const bookmark = wrapper.parentElement;
        if (
          bookmark instanceof HTMLElement &&
          bookmark.id.endsWith("-content") &&
          bookmark.getAttribute("tabindex") === "-1"
        ) {
          bookmark.removeAttribute("tabindex");
        }
      });
  }

  /**
   * Relocate a toast under the webform as a text banner.
   * Lien focusé une fois, AJAX comme postback (voir en-tête de fichier).
   *
   * @param {"success"|"error"} kind
   * @param {string} toastSelector
   * @param {boolean} ajaxMode
   * @return {boolean}
   */
  function relocateToastUnderForm(kind, toastSelector, ajaxMode) {
    const toast = document.querySelector(toastSelector);
    if (!(toast instanceof HTMLElement)) {
      return false;
    }

    const text = readToastText(toast);
    if (!text) {
      return false;
    }

    const host = findWebformFeedbackHost();
    if (!host) {
      return false;
    }

    const signature = feedbackSignature(kind, text);
    const existing = host.querySelector(`:scope > .${FEEDBACK_CLASS}`);
    const existingAction =
      existing instanceof HTMLElement
        ? existing.querySelector(`.${FEEDBACK_CLASS}__action`)
        : null;

    // Same message already shown: keep DOM, do not re-announce.
    // On reprend juste le focus s'il a été volé (toast, skip-link).
    if (
      signature === lastFeedbackSignature &&
      existing instanceof HTMLElement &&
      existing.classList.contains(`${FEEDBACK_CLASS}--${kind}`)
    ) {
      muteRelocatableToasts();
      if (existingAction instanceof HTMLElement) {
        claimFocus(existingAction);
      }
      return true;
    }

    muteRelocatableToasts();

    let message = existing;
    if (!(message instanceof HTMLElement)) {
      message = document.createElement("div");
      message.className = FEEDBACK_CLASS;
      host.appendChild(message);
    }

    message.classList.remove(
      `${FEEDBACK_CLASS}--success`,
      `${FEEDBACK_CLASS}--error`
    );
    message.classList.add(`${FEEDBACK_CLASS}--${kind}`);

    message.textContent = "";
    message.removeAttribute("role");
    message.removeAttribute("aria-modal");
    message.removeAttribute("aria-label");
    message.removeAttribute("aria-labelledby");
    message.removeAttribute("aria-live");
    message.removeAttribute("aria-atomic");
    message.removeAttribute("tabindex");

    // Lien focusé : interrompt la lecture en cours, et un élément
    // interactif arrête la lecture après le message.
    if (!message.id || !isSafeDomId(message.id)) {
      message.id = `ssks-form-feedback-${safeDomToken(host.id)}`;
    }
    const action = document.createElement("a");
    action.href = safeHashHref(message.id) || `#${FEEDBACK_CLASS}`;
    action.className = `${FEEDBACK_CLASS}__action`;
    action.textContent = text;
    // Keep focus on the message; do not jump to the hash target.
    action.addEventListener("click", (event) => {
      event.preventDefault();
    });
    message.appendChild(action);

    lastFeedbackSignature = signature;
    scrollBelowSticky(message, "auto");
    if (ajaxMode) {
      // Laisse finir l'animation de scroll webform (500 ms, remonte en haut
      // du formulaire) puis revient sur le bandeau.
      window.setTimeout(() => {
        if (message.isConnected) {
          scrollBelowSticky(message, "auto");
        }
      }, 600);
    }
    claimFocus(action);
    return true;
  }

  function clearOrphanFeedback(host) {
    if (!(host instanceof HTMLElement)) {
      return;
    }
    host.querySelector(`:scope > .${FEEDBACK_CLASS}`)?.remove();
    lastFeedbackSignature = "";
  }

  function handleStatusFeedback(ajaxMode) {
    const host = findWebformFeedbackHost();
    if (!host) {
      return false;
    }
    // Field errors: the summary owns the page — do not relocate status.
    if (document.querySelector(FIELD_ERROR_SELECTOR)) {
      clearOrphanFeedback(host);
      return false;
    }

    const errorToast = document.querySelector(ERROR_TOAST_SELECTOR);
    const successToast = document.querySelector(SUCCESS_TOAST_SELECTOR);
    const hasError = errorToast instanceof HTMLElement;
    const hasSuccess = successToast instanceof HTMLElement;

    if (hasError) {
      return relocateToastUnderForm("error", ERROR_TOAST_SELECTOR, ajaxMode);
    }
    if (hasSuccess) {
      return relocateToastUnderForm(
        "success",
        SUCCESS_TOAST_SELECTOR,
        ajaxMode
      );
    }

    // No toast left → drop a stale under-form banner.
    clearOrphanFeedback(host);
    return false;
  }

  const scheduleHandle = createScheduler(() => {
    if (
      findWebformFeedbackHost() &&
      !document.querySelector(FIELD_ERROR_SELECTOR) &&
      (document.querySelector(ERROR_TOAST_SELECTOR) ||
        document.querySelector(SUCCESS_TOAST_SELECTOR))
    ) {
      muteRelocatableToasts();
    }
    handleStatusFeedback(!!ajaxPhase);
    ajaxPhase = false;
  });

  Drupal.behaviors.ssksFormWebformFeedback = {
    attach() {
      if (!findWebformFeedbackHost()) {
        return;
      }
      disarmWebformFocusBookmark();
      scheduleHandle();
    },
  };

  document.addEventListener(
    "submit",
    (event) => {
      if (!(event.target instanceof HTMLFormElement)) {
        return;
      }
      if (
        event.target.classList.contains("webform-submission-form") ||
        event.target.matches("[data-drupal-selector*='webform']")
      ) {
        lastFeedbackSignature = "";
      } else if (event.target.method === "post") {
        // Envoi classique : le message arrivera en toast après rechargement.
        rememberSubmittedForm(event.target);
      }
    },
    true
  );

  $(document).on("ajaxComplete.ssksFormWebformFeedback", () => {
    if (!findWebformFeedbackHost()) {
      return;
    }
    const needsWork =
      document.querySelector(ERROR_TOAST_SELECTOR) ||
      document.querySelector(SUCCESS_TOAST_SELECTOR) ||
      document.querySelector(`.${FEEDBACK_CLASS}`) ||
      document.querySelector(FIELD_ERROR_SELECTOR);
    if (!needsWork) {
      return;
    }
    ajaxPhase = true;
    scheduleHandle();
  });
})(Drupal, jQuery);
